"use client";

import { useState } from "react";
import Image from "next/image";
import { Mail, ArrowLeft } from "lucide-react";
import Link from "next/link";
import { useLogo } from "@/lib/useLogo";
import { useT } from "@/lib/cms-locale";

export default function ForgotPasswordPage() {
  const { logoUrl, loginBgUrl } = useLogo();
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (res.ok) {
        setSent(true);
      } else {
        const data = await res.json();
        setError(data.error || t("forgot.error"));
      }
    } catch {
      setError(t("auth.connectionError"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center">
      {/* Fullscreen background */}
      {loginBgUrl ? (
        <>
          <Image
            src={loginBgUrl}
            alt=""
            fill
            className="object-cover"
            priority
            unoptimized
          />
          <div className="absolute inset-0 bg-black/50" />
        </>
      ) : (
        <div className="absolute inset-0 bg-sidebar" />
      )}

      {/* Box */}
      <div className="relative z-10 w-full max-w-[420px] rounded-2xl bg-black/40 px-10 py-12 shadow-2xl backdrop-blur-md mx-4">
        {/* Logo */}
        <div className="mb-8 text-center">
          {logoUrl ? (
            <div className="mx-auto mb-4 flex items-center justify-center">
              <Image src={logoUrl} alt="Logo" width={140} height={42} className="h-[42px] w-auto object-contain" unoptimized />
            </div>
          ) : (
            <>
              <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15">
                <span className="text-xl font-bold text-primary">L</span>
              </div>
              <h1 className="text-xl font-bold text-white">lividea</h1>
            </>
          )}
        </div>

        {sent ? (
          /* Success State */
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20">
              <Mail size={20} className="text-green-400" />
            </div>
            <h2 className="mb-2 text-lg font-semibold text-white">
              {t("forgot.success")}
            </h2>
            <p className="mb-6 text-sm text-white/50">
              {t("forgot.successMsg")}
            </p>
            <Link
              href="/login"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
            >
              <ArrowLeft size={16} />
              {t("forgot.backToLogin")}
            </Link>
          </div>
        ) : (
          /* Form State */
          <>
            <p className="mb-4 text-sm text-white/50">
              {t("forgot.description")}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="mb-1 block text-xs font-medium text-white/60">
                  {t("auth.email")}
                </label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    autoComplete="email"
                    className="w-full rounded-lg border border-white/20 bg-white pl-10 pr-3 py-2.5 text-sm text-foreground placeholder-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder={t("auth.emailPlaceholder")}
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-600">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-sidebar transition-colors hover:bg-primary/80 disabled:opacity-50"
              >
                {loading ? t("forgot.sending") : t("forgot.send")}
              </button>
            </form>

            <div className="mt-4 text-center">
              <Link
                href="/login"
                className="inline-flex items-center gap-1 text-sm text-white/50 hover:text-primary"
              >
                <ArrowLeft size={14} />
                {t("forgot.backToLogin")}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
